/**
 * Intake Form Schema - Drives dynamic form rendering
 *
 * This file provides TypeScript types, a React hook for loading the schema
 * from the backend API, and helper functions for the intake form.
 *
 * The JSON schema is the source of truth, served by the backend API.
 */

import { useState, useEffect, useRef } from 'react';
import { getIntakeFormSchemaIntakeFormSchemaGet } from '../services/api/sdk.gen';

// =============================================================================
// Type Definitions
// =============================================================================

/** Supported field types for rendering */
export type IntakeFieldType =
  | 'text' // Field component - single line input
  | 'date' // DateField component - date picker
  | 'boolean' // BooleanField component - Yes/No/N/A dropdown
  | 'list' // Field with array handling - comma-separated values
  | 'dropdown' // DropdownField for special cases like agency_insurance
  | 'object_array'; // Generic renderer for medications-like repeated structures

/** Validator function names (registered in validators.ts) */
export type ValidatorFn =
  | 'validate_first_name' // max 19 chars
  | 'validate_last_name' // max 20 chars
  | 'validate_ssn' // XXX-XX-XXXX pattern
  | 'validate_phone' // aaa-bbb-cccc pattern
  | 'validate_email' // email pattern
  | 'validate_zip' // 12345 or 12345-6789
  | 'validate_mbi'; // 11-char MBI, no dashes

/** Formatter function names (registered in formatters.ts) */
export type FormatterFn =
  'format_ssn' | 'format_phone' | 'format_zip' | 'format_mbi';

/** Dynamic dropdown sources */
export type DropdownSource = 'agency_insurance' | 'agency_clinic';

/** Schema for object_array item fields */
export interface ObjectArrayItemField {
  field_id: string;
  label?: string;
  type: 'text' | 'date';
  columns: number; // Out of 12 (grid-cols-12)
  placeholder?: string;
}

/** Schema for object_array fields (like medications) */
export interface ObjectArrayFieldSchema {
  item_label: string;
  empty_state_icon?: string; // Lucide icon name
  empty_state_title: string;
  empty_state_subtitle: string;
  add_button_text: string;
  max_height?: string;
  fields: ObjectArrayItemField[];
  api_config: {
    create_endpoint: string;
    update_endpoint: string;
    delete_endpoint: string;
    default_values: Record<string, unknown>;
  };
}

/** Single field definition */
export interface IntakeFieldDefinition {
  field_id: string;
  label: string;
  type: IntakeFieldType;
  columns?: number; // Grid columns (1-4), default 1
  required?: boolean;
  validator_fn?: ValidatorFn;
  validator_config?: Record<string, unknown>; // Config for validators/UI (e.g., fromYear, toYear for dates)
  formatter_fn?: FormatterFn;
  description?: string;
  more_context?: string;
  multiline?: boolean;
  options?: Array<{ value: string; label: string }>;
  dropdown_source?: DropdownSource;
  object_array_schema?: ObjectArrayFieldSchema;
}

/** Section display configuration */
export interface SectionDisplay {
  columns: number; // Grid columns for this section (typically 3 or 4)
  position: number; // Order in the form (1-based)
  hidden?: boolean; // If true, section is not rendered (used for extraction-only fields)
}

/** Section definition */
export interface IntakeSectionDefinition {
  display: SectionDisplay;
  section_name: string;
  content: IntakeFieldDefinition[];
}

/** Complete schema type */
export interface IntakeFormSchema {
  [section_key: string]: IntakeSectionDefinition;
}

// =============================================================================
// Schema Loading
// =============================================================================

// Module-level cache for the schema
let cachedSchema: IntakeFormSchema | null = null;
let schemaPromise: Promise<IntakeFormSchema> | null = null;

/**
 * Apply runtime adjustments to the schema (e.g., dynamic year ranges)
 */
const applyRuntimeAdjustments = (
  schema: IntakeFormSchema
): IntakeFormSchema => {
  const currentYear = new Date().getFullYear();
  const patientIdSection = schema.patient_identification;
  if (patientIdSection) {
    const dobField = patientIdSection.content.find(
      f => f.field_id === 'patient_dob'
    );
    if (dobField && dobField.validator_config) {
      dobField.validator_config.fromYear = currentYear - 120;
      dobField.validator_config.toYear = currentYear;
    }
  }
  return schema;
};

/**
 * Fetch the schema from the API and cache it
 */
export const fetchIntakeFormSchema = async (): Promise<IntakeFormSchema> => {
  // Return cached schema if available
  if (cachedSchema) {
    return cachedSchema;
  }

  // If a fetch is already in progress, wait for it
  if (schemaPromise) {
    return schemaPromise;
  }

  // Start the fetch
  schemaPromise = (async () => {
    const response = await getIntakeFormSchemaIntakeFormSchemaGet({});
    if (!response.data) {
      throw new Error('Failed to fetch intake form schema');
    }
    const schema = applyRuntimeAdjustments(response.data as IntakeFormSchema);
    cachedSchema = schema;
    return schema;
  })();

  return schemaPromise;
};

/**
 * Get the cached schema synchronously.
 * Throws if the schema hasn't been loaded yet.
 */
export const getCachedSchema = (): IntakeFormSchema => {
  if (!cachedSchema) {
    throw new Error(
      'Intake form schema not loaded. Use useIntakeFormSchema hook or call fetchIntakeFormSchema first.'
    );
  }
  return cachedSchema;
};

/**
 * Check if the schema is loaded
 */
export const isSchemaLoaded = (): boolean => {
  return cachedSchema !== null;
};

// =============================================================================
// React Hook
// =============================================================================

export interface UseIntakeFormSchemaResult {
  schema: IntakeFormSchema | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * React hook to load and access the intake form schema
 */
export const useIntakeFormSchema = (): UseIntakeFormSchemaResult => {
  const [schema, setSchema] = useState<IntakeFormSchema | null>(cachedSchema);
  const [isLoading, setIsLoading] = useState(!cachedSchema);
  const [error, setError] = useState<Error | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    // If already cached, we're done
    if (cachedSchema) {
      setSchema(cachedSchema);
      setIsLoading(false);
      return;
    }

    // Fetch the schema
    fetchIntakeFormSchema()
      .then(loadedSchema => {
        if (isMounted.current) {
          setSchema(loadedSchema);
          setIsLoading(false);
        }
      })
      .catch(err => {
        if (isMounted.current) {
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted.current = false;
    };
  }, []);

  return { schema, isLoading, error };
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get sections sorted by position for rendering.
 * Filters out hidden sections (used for extraction-only fields).
 */
export const getSortedSections = (
  schema?: IntakeFormSchema
): Array<[string, IntakeSectionDefinition]> => {
  const schemaToUse = schema ?? getCachedSchema();
  return Object.entries(schemaToUse)
    .filter(([, section]) => !section.display.hidden)
    .sort(([, a], [, b]) => a.display.position - b.display.position);
};

/**
 * Get all required fields from schema
 */
export const getRequiredFields = (
  schema?: IntakeFormSchema
): IntakeFieldDefinition[] => {
  const schemaToUse = schema ?? getCachedSchema();
  const required: IntakeFieldDefinition[] = [];
  Object.values(schemaToUse).forEach(section => {
    section.content.forEach(field => {
      if (field.required) {
        required.push(field);
      }
    });
  });
  return required;
};

/**
 * Get field definition by field_id
 */
export const getFieldDefinition = (
  fieldId: string,
  schema?: IntakeFormSchema
): IntakeFieldDefinition | undefined => {
  const schemaToUse = schema ?? getCachedSchema();
  for (const section of Object.values(schemaToUse)) {
    const field = section.content.find(f => f.field_id === fieldId);
    if (field) return field;
  }
  return undefined;
};

/**
 * For a dropdown field, return the human-readable label for a stored value.
 * Falls back to the raw value if the field or option is not found.
 */
export const getDropdownOptionLabel = (
  schema: IntakeFormSchema,
  fieldId: string,
  value: string
): string => {
  const field = getFieldDefinition(fieldId, schema);
  if (!field?.options) return value;
  return field.options.find(o => o.value === value)?.label ?? value;
};
